// Isolated adapter + payload builder for the Character Show branded-audio
// worker. Mapped to the ACTUAL canonical handler contract (OpenAPI declares an
// empty GET schema; shape confirmed from backend handlers):
//   GET  /api/v1/admin/characters/voices
//   POST /api/v1/admin/characters/voices/config
// Both return:
//   { provider_configured, provider_kill, worker_enabled, autogen_new_pregame,
//     worker_running,
//     usage: { date, characters, daily_char_limit, remaining, calls },
//     last_provider_error: null | { class, http_status, category, code, type,
//                                    episode_id, game_id, at },
//     voices: [...], env_voice_ids: {...} }
// The console only flips the worker on/off: the toggle POSTs ONLY { enabled }
// so the daily cap (usage.daily_char_limit) and autogen_new_pregame are
// preserved by the backend. The view-model surfaces nothing secret-like and
// limits the last provider error to a safe category + timestamp.

export const VOICE_NOT_REPORTED = "not reported";

// Start/pause payload — ONLY { enabled } by contract. No cap, no autogen, no
// provider fields, so a toggle can never clobber the preserved settings.
export function buildVoiceTogglePayload(on) {
  return { enabled: !!on };
}

function asBoolOrNull(v) {
  return typeof v === "boolean" ? v : null;
}
function asNumOrNull(v) {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
function asStrOrNull(v) {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

// Pure view-model. `raw` is the GET /admin/characters/voices payload (or the
// identical POST config read-back). Missing fields read null so the UI renders
// "not reported".
export function normalizeVoiceWorker(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const usage = r.usage && typeof r.usage === "object" ? r.usage : {};
  const err = r.last_provider_error && typeof r.last_provider_error === "object" ? r.last_provider_error : null;

  const providerConfigured = asBoolOrNull(r.provider_configured);
  const providerKill = asBoolOrNull(r.provider_kill);
  const enabled = asBoolOrNull(r.worker_enabled);
  const running = asBoolOrNull(r.worker_running);
  const autogenNewPregame = asBoolOrNull(r.autogen_new_pregame);

  const dailyUsed = asNumOrNull(usage.characters);
  const dailyCap = asNumOrNull(usage.daily_char_limit);
  const remaining = asNumOrNull(usage.remaining);
  const calls = asNumOrNull(usage.calls);
  const usageDate = asStrOrNull(usage.date);

  // Last provider error: expose ONLY a safe category + timestamp. Never surface
  // episode_id/game_id/http_status/class/code/type here.
  const lastError = err ? { category: asStrOrNull(err.category) ?? VOICE_NOT_REPORTED, at: asStrOrNull(err.at) } : null;

  let state;
  if (providerKill === true) state = "killed";
  else if (enabled === true) state = "active";
  else if (enabled === false) state = "paused";
  else state = "unknown";

  return {
    reported: Object.keys(r).length > 0,
    providerConfigured,
    providerKill,
    enabled,
    running,
    autogenNewPregame,
    dailyUsed,
    dailyCap,
    remaining,
    calls,
    usageDate,
    lastError,
    state,
  };
}

// Read-back guard: a toggle is only "confirmed" when the backend read-back
// reports worker_enabled === the requested state. Prevents a false success toast
// when the GET fails, is malformed, or disagrees with the request.
export function voiceToggleConfirmed(vm, requestedOn) {
  return !!vm && vm.enabled === !!requestedOn;
}
